import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useExam } from '../hooks/useExam';
import { EGZAMIN, GRUPA_PO_SLUGU, LITERY, PATENT_PO_SLUGU, kluczeOdpowiedzi } from '../data/patenty';
import { odmien } from '../lib/odmiana';
import { podstawaPrawna } from '../lib/podstawa';
import MarkdownRenderer from './MarkdownRenderer';
import styles from '../css/modules/ExamSimulator.module.css';

/**
 * Symulator egzaminu teoretycznego na patent żeglarski.
 *
 * Odwzorowuje prawdziwy egzamin PZŻ/PZMWiNW: 75 pytań, 90 minut, próg 65
 * poprawnych odpowiedzi. W przeciwieństwie do trybu nauki nie pokazuje
 * poprawnej odpowiedzi od razu - wynik i omówienie pojawiają się dopiero po
 * oddaniu arkusza, tak jak na egzaminie.
 *
 * Stan (odpowiedzi, oznaczone pytania, moment startu) trzymamy w localStorage,
 * bo egzamin trwa półtorej godziny i przypadkowe odświeżenie strony nie może
 * kasować pracy. Klucz zawiera patent, żeby dwa egzaminy nie nadpisywały się
 * nawzajem.
 */

const klucz = patent => `zeglarka_egzamin_${patent}`;

const formatCzas = sekundy => {
  const s = Math.max(0, Math.round(sekundy));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

const ExamSimulator = () => {
  const { patent: patentSlug } = useParams();
  const navigate = useNavigate();
  const patent = PATENT_PO_SLUGU[patentSlug];

  const { questions: pobrane, error, loading, demo } = useExam({ patent: patentSlug });

  // Arkusz trzymamy osobno od wyniku pobrania. Pełny egzamin jest LOSOWANY przy
  // każdym zapytaniu, więc po odświeżeniu strony z API wraca inny zestaw pytań.
  // Gdyby ekran renderował wprost `pobrane`, zapisana sesja nigdy by się nie
  // odtworzyła - odpowiedzi dotyczyłyby pytań, których już nie ma na ekranie.
  // Dlatego zapisujemy cały arkusz i po powrocie ma on pierwszeństwo.
  const [arkusz, setArkusz] = useState([]);
  const [odpowiedzi, setOdpowiedzi] = useState({});
  const [oznaczone, setOznaczone] = useState([]);
  const [biezace, setBiezace] = useState(0);
  const [startAt, setStartAt] = useState(null);
  const [oddany, setOddany] = useState(false);
  const [teraz, setTeraz] = useState(() => Date.now());
  const przywrocone = useRef(false);
  const kartaRef = useRef(null);
  const przewinDoPytania = useRef(false);

  const questions = arkusz;
  const limitSekund = (demo ? 20 : EGZAMIN.minut) * 60;

  /* --- przywrócenie sesji ------------------------------------------------ */
  useEffect(() => {
    if (przywrocone.current || !patentSlug || !pobrane.length) return;
    przywrocone.current = true;
    try {
      const zapis = JSON.parse(localStorage.getItem(klucz(patentSlug)) || 'null');
      const wciazWazny = zapis && Array.isArray(zapis.pytania) && zapis.pytania.length
        && !zapis.oddany
        && Date.now() - (zapis.startAt || 0) < limitSekund * 1000;
      if (wciazWazny) {
        setArkusz(zapis.pytania);
        setOdpowiedzi(zapis.odpowiedzi || {});
        setOznaczone(zapis.oznaczone || []);
        setStartAt(zapis.startAt);
        return;
      }
    } catch (e) {
      /* uszkodzony zapis ignorujemy i zaczynamy od nowa */
    }
    setArkusz(pobrane);
    setStartAt(Date.now());
  }, [patentSlug, pobrane, limitSekund]);

  /* --- zapis sesji ------------------------------------------------------- */
  useEffect(() => {
    if (!startAt || !arkusz.length) return;
    try {
      localStorage.setItem(klucz(patentSlug), JSON.stringify({
        pytania: arkusz,
        odpowiedzi,
        oznaczone,
        startAt,
        oddany,
      }));
    } catch (e) {
      // Przy pełnym localStorage nie ma sensu wywracać egzaminu - trudno,
      // sesja po prostu nie przetrwa odświeżenia.
    }
  }, [patentSlug, arkusz, odpowiedzi, oznaczone, startAt, oddany]);

  /* --- zegar ------------------------------------------------------------- */
  useEffect(() => {
    if (!startAt || oddany) return undefined;
    const id = setInterval(() => setTeraz(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startAt, oddany]);

  const pozostalo = startAt ? limitSekund - (teraz - startAt) / 1000 : limitSekund;

  /* Po zmianie pytania karta ma być na ekranie. Na wąskim telefonie przyciski
     nawigacji stoją poniżej pierwszego ekranu, więc bez tego kliknięcie
     "Następne" zostawiało użytkownika przy przyciskach, a treść nowego pytania
     lądowała nad widokiem - strona zdawała się zjeżdżać coraz niżej. */
  useEffect(() => {
    if (!przewinDoPytania.current || !kartaRef.current) return;
    przewinDoPytania.current = false;
    const gora = kartaRef.current.getBoundingClientRect().top;
    if (gora < 0 || gora > window.innerHeight * 0.5) {
      kartaRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [biezace]);

  /* Wynik renderuje się na górze strony, a przycisk "Oddaj arkusz" stoi na
     samym dole - bez tego użytkownik po oddaniu arkusza widział listę błędów
     zamiast własnego wyniku. */
  useEffect(() => {
    if (oddany) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [oddany]);

  useEffect(() => {
    if (!oddany && startAt && pozostalo <= 0) setOddany(true);
  }, [pozostalo, oddany, startAt]);

  /* --- wynik ------------------------------------------------------------- */
  const wynik = useMemo(() => {
    if (!questions.length) return null;
    const poprawne = questions.filter(q => odpowiedzi[q.slug] === q.correctAnswer);
    const wgGrupy = {};
    questions.forEach(q => {
      const g = q.grupa || 'inne';
      wgGrupy[g] = wgGrupy[g] || { razem: 0, dobrze: 0 };
      wgGrupy[g].razem += 1;
      if (odpowiedzi[q.slug] === q.correctAnswer) wgGrupy[g].dobrze += 1;
    });
    // Próg skalujemy proporcjonalnie, żeby demo (15 pytań) też miało sens.
    const prog = Math.ceil((EGZAMIN.prog / EGZAMIN.pytan) * questions.length);
    return {
      dobrze: poprawne.length,
      razem: questions.length,
      prog,
      zdany: poprawne.length >= prog,
      wgGrupy,
      bledy: questions.filter(q => odpowiedzi[q.slug] !== q.correctAnswer),
    };
  }, [questions, odpowiedzi]);

  const idzDoPytania = useCallback(indeks => {
    przewinDoPytania.current = true;
    setBiezace(indeks);
  }, []);

  const zaznacz = useCallback((slug, key) => {
    if (oddany) return;
    setOdpowiedzi(prev => ({ ...prev, [slug]: key }));
  }, [oddany]);

  const przelaczOznaczenie = useCallback(slug => {
    setOznaczone(prev => (prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]));
  }, []);

  const odNowa = () => {
    // Nowy zestaw musi przyjść z API, więc przeładowujemy stronę - inaczej
    // dostalibyśmy ten sam arkusz odtworzony z zapisu.
    localStorage.removeItem(klucz(patentSlug));
    window.location.reload();
  };

  /* --- stany brzegowe ---------------------------------------------------- */
  if (!patent) {
    return (
      <div className={styles.wrap}>
        <h1>Nie znamy takiego patentu</h1>
        <p>Wybierz jeden z dostępnych egzaminów próbnych.</p>
        <Link className={styles.cta} to="/">Wróć na stronę główną</Link>
      </div>
    );
  }

  // Arkusz ustawia efekt, który biegnie dopiero po commicie - bez tego warunku
  // między zakończeniem pobierania a ustawieniem stanu mignąłby ekran błędu.
  if (loading || (!arkusz.length && pobrane.length)) {
    return <div className={styles.wrap}><p className={styles.info}>Przygotowujemy arkusz egzaminacyjny…</p></div>;
  }

  if (error || !questions.length) {
    return (
      <div className={styles.wrap}>
        <h1>Nie udało się wczytać pytań</h1>
        <p className={styles.info}>{error || 'Baza pytań dla tego patentu jest chwilowo niedostępna.'}</p>
        <button type="button" className={styles.cta} onClick={() => window.location.reload()}>Spróbuj ponownie</button>
      </div>
    );
  }

  /* --- ekran wyniku ------------------------------------------------------ */
  if (oddany && wynik) {
    return (
      <div className={styles.wrap}>
        <div className={`${styles.wynikBox} ${wynik.zdany ? styles.zdany : styles.niezdany}`}>
          <p className={styles.wynikEtykieta}>{wynik.zdany ? 'Egzamin zdany' : 'Egzamin niezdany'}</p>
          <p className={styles.wynikLiczba}>{wynik.dobrze} / {wynik.razem}</p>
          <p className={styles.wynikProg}>
            Próg zaliczenia: {wynik.prog} {odmien(wynik.prog, ['poprawna odpowiedź', 'poprawne odpowiedzi', 'poprawnych odpowiedzi'])}
          </p>
        </div>

        <h2 className={styles.naglowek}>Wynik według działów</h2>
        <ul className={styles.grupyLista}>
          {Object.entries(wynik.wgGrupy).map(([g, v]) => {
            const procent = Math.round((v.dobrze / v.razem) * 100);
            return (
              <li key={g} className={styles.grupaWiersz}>
                <span className={styles.grupaNazwa}>{GRUPA_PO_SLUGU[g]?.nazwa || g}</span>
                <span className={styles.grupaPasek}>
                  <span className={styles.grupaWypelnienie} style={{ width: `${procent}%` }} />
                </span>
                <span className={styles.grupaLiczba}>{v.dobrze}/{v.razem}</span>
              </li>
            );
          })}
        </ul>

        {wynik.bledy.length > 0 && (
          <>
            <h2 className={styles.naglowek}>
              Do powtórki: {wynik.bledy.length} {odmien(wynik.bledy.length, ['pytanie', 'pytania', 'pytań'])}
            </h2>
            <ol className={styles.bledyLista}>
              {wynik.bledy.map(q => (
                <li key={q.slug} className={styles.blad}>
                  <p className={styles.bladPytanie}>{q.question}</p>
                  <p className={styles.bladZla}>
                    {odpowiedzi[q.slug]
                      ? <>Twoja odpowiedź: <strong>{LITERY[odpowiedzi[q.slug]]}</strong> {q[odpowiedzi[q.slug]]}</>
                      : 'Brak odpowiedzi'}
                  </p>
                  <p className={styles.bladDobra}>
                    Poprawna: <strong>{LITERY[q.correctAnswer]}</strong> {q[q.correctAnswer]}
                  </p>
                  {q.explanation && (
                    <div className={styles.bladWyjasnienie}><MarkdownRenderer content={q.explanation} /></div>
                  )}
                  {podstawaPrawna(q) && (
                    <p className={styles.bladPodstawa}>Podstawa prawna: {podstawaPrawna(q)}</p>
                  )}
                  <Link className={styles.bladLink} to={`/pytania/${q.slug}`}>Omówienie tego pytania</Link>
                </li>
              ))}
            </ol>
          </>
        )}

        <div className={styles.akcje}>
          <button type="button" className={styles.cta} onClick={odNowa}>Rozwiąż nowy zestaw</button>
          {demo && (
            <button type="button" className={styles.ctaDrugi} onClick={() => navigate('/cennik')}>
              Odblokuj pełną bazę pytań
            </button>
          )}
        </div>
      </div>
    );
  }

  /* --- arkusz ------------------------------------------------------------ */
  const pytanie = questions[biezace];
  const klucze = kluczeOdpowiedzi(pytanie);
  const udzielone = Object.keys(odpowiedzi).length;
  const malo = pozostalo <= 300;

  return (
    <div className={styles.wrap}>
      <div className={styles.pasek}>
        <div>
          {/* H1 strony to jej temat, a nie pierwsze wylosowane pytanie.
              Prerender zapisuje HTML z losowym zestawem, więc dotąd każdy
              zrzut /egzamin/<patent> dostawał inny nagłówek pierwszego rzędu
              - a robot i czytnik ekranu widziały pytanie zamiast tytułu. */}
          <h1 className={styles.paskaTytul}>Egzamin próbny: {patent.nazwa}</h1>
          <p className={styles.paskaPodtytul}>
            {udzielone} z {questions.length} {odmien(questions.length, ['pytanie', 'pytania', 'pytań'])} z odpowiedzią
            {demo && ' · wersja demo'}
          </p>
        </div>
        <div className={`${styles.zegar} ${malo ? styles.zegarAlarm : ''}`} aria-live="polite">
          {formatCzas(pozostalo)}
        </div>
      </div>

      <div className={styles.siatka} role="navigation" aria-label="Nawigacja po pytaniach">
        {questions.map((q, i) => {
          const kl = [styles.siatkaBtn];
          if (odpowiedzi[q.slug]) kl.push(styles.siatkaOdp);
          if (oznaczone.includes(q.slug)) kl.push(styles.siatkaFlaga);
          if (i === biezace) kl.push(styles.siatkaAktywne);
          return (
            <button
              key={q.slug}
              type="button"
              className={kl.join(' ')}
              onClick={() => idzDoPytania(i)}
              aria-current={i === biezace ? 'true' : undefined}
              aria-label={`Pytanie ${i + 1}${odpowiedzi[q.slug] ? ', odpowiedziane' : ''}`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>

      <div className={styles.karta} ref={kartaRef}>
        <p className={styles.numer}>
          Pytanie {biezace + 1} z {questions.length}
          {pytanie.grupa && <span className={styles.grupaTag}>{GRUPA_PO_SLUGU[pytanie.grupa]?.nazwa || pytanie.grupa}</span>}
        </p>
        <h2 className={styles.pytanie}>{pytanie.question}</h2>

        <div className={styles.odpowiedzi}>
          {klucze.map(k => (
            <button
              key={k}
              type="button"
              className={`${styles.odpowiedz} ${odpowiedzi[pytanie.slug] === k ? styles.wybrana : ''}`}
              onClick={() => zaznacz(pytanie.slug, k)}
            >
              <span className={styles.litera}>{LITERY[k]}</span>
              <span>{pytanie[k]}</span>
            </button>
          ))}
        </div>

        <div className={styles.nawigacja}>
          <button
            type="button"
            className={styles.nawBtn}
            onClick={() => idzDoPytania(Math.max(0, biezace - 1))}
            disabled={biezace === 0}
          >
            Poprzednie
          </button>
          <button
            type="button"
            className={`${styles.nawBtn} ${oznaczone.includes(pytanie.slug) ? styles.nawBtnAktywny : ''}`}
            onClick={() => przelaczOznaczenie(pytanie.slug)}
          >
            {oznaczone.includes(pytanie.slug) ? 'Odznacz' : 'Oznacz do powrotu'}
          </button>
          <button
            type="button"
            className={styles.nawBtn}
            onClick={() => idzDoPytania(Math.min(questions.length - 1, biezace + 1))}
            disabled={biezace === questions.length - 1}
          >
            Następne
          </button>
        </div>
      </div>

      <div className={styles.akcje}>
        <button type="button" className={styles.cta} onClick={() => setOddany(true)}>
          Oddaj arkusz i zobacz wynik
        </button>
        {udzielone < questions.length && (
          <p className={styles.info}>
            Bez odpowiedzi: {questions.length - udzielone}{' '}
            {odmien(questions.length - udzielone, ['pytanie', 'pytania', 'pytań'])}
          </p>
        )}
      </div>
    </div>
  );
};

export default ExamSimulator;
